import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import { OCCURRENCE, OC_STATUS, TRATAMENTO, TRATAMENTO_ORDER, fmtDate } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, ArrowRight, Check, PlayCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_FILTERS = [
  { id: "", label: "Todas" },
  { id: "ABERTA", label: "Abertas" },
  { id: "EM_TRATAMENTO", label: "Em Tratamento" },
  { id: "RESOLVIDA", label: "Resolvidas" },
];

export default function Ocorrencias() {
  const [ocs, setOcs] = useState(null);
  const [searchParams] = useSearchParams();
  const [tipo, setTipo] = useState(searchParams.get("tipo") || "");
  const [status, setStatus] = useState(searchParams.get("status") || "");
  const [busy, setBusy] = useState(false);
  const [resolver, setResolver] = useState(null); // ocorrência sendo resolvida
  const [solucao, setSolucao] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    setTipo(searchParams.get("tipo") || "");
    setStatus(searchParams.get("status") || "");
  }, [searchParams]);

  const load = useCallback(() => {
    const params = [];
    if (tipo) params.push(`tipo=${tipo}`);
    if (status) params.push(`status=${status}`);
    setOcs(null);
    api.get(`/ocorrencias${params.length ? "?" + params.join("&") : ""}`).then((r) => setOcs(r.data));
  }, [tipo, status]);
  useEffect(load, [load]);

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro na operação"); }
    finally { setBusy(false); }
  };

  const iniciarTratamento = (o) => act(async () => {
    await api.post(`/ocorrencias/${o.id}/status`, { status: "EM_TRATAMENTO" });
    toast.success("Ocorrência em tratamento");
  });

  const confirmarResolver = () => act(async () => {
    await api.post(`/ocorrencias/${resolver.id}/status`, { status: "RESOLVIDA", solucao });
    setResolver(null); setSolucao("");
    toast.success("Ocorrência resolvida");
  });

  const avancarTratamento = (o) => act(async () => {
    const prox = TRATAMENTO[o.tratamento || "AGUARDANDO_DEVOLUCAO"].next;
    const { data } = await api.post(`/ocorrencias/${o.id}/tratamento`, { tratamento: prox });
    toast.success(data.tratamento === "RECEBIDO_CD" ? "Produto recebido no CD — estoque creditado" : `Tratamento: ${TRATAMENTO[data.tratamento].label}`);
  });

  return (
    <div className="space-y-5" data-testid="ocorrencias-page">
      <div>
        <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Condições Vinculadas</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase">Ocorrências</h1>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button variant={tipo === "" ? "default" : "outline"} size="sm" onClick={() => setTipo("")}>Todos tipos</Button>
        {Object.keys(OCCURRENCE).map((t) => (
          <Button key={t} variant={tipo === t ? "default" : "outline"} size="sm" onClick={() => setTipo(t)} data-testid={`oc-filtro-${t}`}>{OCCURRENCE[t].label}</Button>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap">
        {STATUS_FILTERS.map((s) => (
          <Button key={s.id} variant={status === s.id ? "default" : "outline"} size="sm" onClick={() => setStatus(s.id)} data-testid={`oc-status-${s.id || "todas"}`}>{s.label}</Button>
        ))}
      </div>

      {!ocs ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : ocs.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">Nenhuma ocorrência.</Card>
      ) : (
        <div className="grid gap-3">
          {ocs.map((o) => {
            const m = OCCURRENCE[o.tipo] || {};
            const Icon = m.icon;
            const os = OC_STATUS[o.status] || OC_STATUS.ABERTA;
            const isProdErrado = o.subtipo === "PRODUTO_INCORRETO";
            const trat = o.tratamento || "AGUARDANDO_DEVOLUCAO";
            const tratMeta = TRATAMENTO[trat];
            return (
              <Card key={o.id} className="bg-card/70 p-4" data-testid={`oc-${o.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3">
                    <span className={`inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-[10px] font-bold uppercase font-mono ${m.cls}`}>
                      {Icon && <Icon className="w-3 h-3" />} {m.label}
                    </span>
                    <div>
                      <button className="font-mono font-bold hover:text-primary" onClick={() => nav(`/pedidos/${o.pedido_id}`)}>{o.pedido_numero}</button>
                      <div className="text-sm">{o.sku_nome} <span className="text-muted-foreground font-mono text-xs">{o.sku}</span></div>
                      <div className="text-xs text-muted-foreground">{o.motivo}</div>
                      {o.observacao && <div className="text-xs text-muted-foreground">Obs.: {o.observacao}</div>}
                      {o.solicitado != null && <div className="text-xs font-mono">Solic. {o.solicitado} · Aprov. {o.aprovado ?? o.atendido} · Corte {o.quantidade}</div>}
                      {o.subtipo_label && <div className="text-xs font-mono">{o.subtipo_label} · qtd {o.quantidade}</div>}
                      {o.pedido_complementar_numero && (
                        <button onClick={() => o.pedido_complementar_id ? nav(`/pedidos/${o.pedido_complementar_id}`) : nav("/pedidos")} className="mt-1 inline-flex items-center gap-1 text-xs font-mono text-amber-300 hover:underline" data-testid={`oc-comp-${o.id}`}>
                          Complementar: {o.pedido_complementar_numero} <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                      {o.foto_path && <div className="mt-2"><AuthImage path={o.foto_path} className="w-28 h-28 object-cover rounded-md border border-border" alt="ocorrência" /></div>}
                    </div>
                  </div>
                  <div className="text-right space-y-1">
                    <span className={`inline-flex rounded-sm border px-2 py-1 text-[10px] font-bold uppercase font-mono ${os.cls}`} data-testid={`oc-status-badge-${o.id}`}>{os.label}</span>
                    <div className="text-xs text-muted-foreground">{fmtDate(o.created_at)}</div>
                  </div>
                </div>

                {/* Tratamento de produto errado (devolução) */}
                {isProdErrado && (
                  <div className="mt-3 rounded-md border border-border bg-secondary/30 p-3">
                    <div className="text-[10px] uppercase text-muted-foreground mb-2 tracking-wide">Tratamento / Devolução</div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {TRATAMENTO_ORDER.map((tk, i) => {
                        const done = TRATAMENTO_ORDER.indexOf(trat) > i;
                        const cur = trat === tk;
                        return (
                          <span key={tk} className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-sm border ${cur ? "border-primary text-primary bg-primary/10" : done ? "border-emerald-600 text-emerald-300" : "border-border text-muted-foreground"}`}>
                            {done && <Check className="w-3 h-3 inline mr-0.5" />}{TRATAMENTO[tk].label}
                          </span>
                        );
                      })}
                    </div>
                    {tratMeta.next && (
                      <Button size="sm" className="mt-2" disabled={busy} onClick={() => avancarTratamento(o)} data-testid={`oc-trat-avancar-${o.id}`}>
                        <ArrowRight className="w-4 h-4" /> {tratMeta.nextLabel}
                      </Button>
                    )}
                  </div>
                )}

                {/* Ciclo genérico (não produto errado) */}
                {!isProdErrado && o.status !== "RESOLVIDA" && (
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {o.status === "ABERTA" && <Button size="sm" variant="outline" disabled={busy} onClick={() => iniciarTratamento(o)} data-testid={`oc-tratar-${o.id}`}><PlayCircle className="w-4 h-4" /> Iniciar Tratamento</Button>}
                    <Button size="sm" disabled={busy} onClick={() => { setResolver(o); setSolucao(""); }} data-testid={`oc-resolver-${o.id}`}><Check className="w-4 h-4" /> Resolver</Button>
                  </div>
                )}

                {o.status === "RESOLVIDA" && (o.solucao || o.resolvido_por) && (
                  <div className="mt-3 text-xs text-emerald-300/90 border-t border-border pt-2">
                    <b>Resolução:</b> {o.solucao || "—"} · {o.resolvido_por} · {fmtDate(o.resolvido_em)}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal Resolver */}
      <Dialog open={!!resolver} onOpenChange={(v) => !v && setResolver(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">Resolver Ocorrência</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">{resolver?.pedido_numero} · {resolver?.sku_nome}</div>
            <Textarea placeholder="Descreva a solução aplicada" value={solucao} onChange={(e) => setSolucao(e.target.value)} data-testid="oc-solucao" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolver(null)}>Cancelar</Button>
            <Button onClick={confirmarResolver} disabled={busy || !solucao.trim()} data-testid="oc-confirmar-resolver">Confirmar Resolução</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
